import React from 'react';
import { Link } from 'react-router-dom';
import { ComponentSkinProps } from '../../../../types/component-skin';
import SafeImage from '../../../common/SafeImage';
import '../ProductSlider.css';

/**
 * 상품 슬라이더 기본 스킨 - 원본 ProductSlider.component.tsx와 동일한 스타일과 기능
 */
const BasicProductSliderSkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils 
}) => {
  const { t } = utils;
  
  // 데이터 추출 (ProductSliderLogic에서 전달되는 데이터)  
  const { 
    // 상품 데이터
    allProducts,
    loading,
    
    // 무한 슬라이드 관련 상태 
    translateX,
    slideWidth,
    isTransitioning,
    isMobile,
    isTablet,
    innerContainerWidth,
    
    // 터치/스와이프 상태
    touchStart,
    touchEnd,
    
    // 컴포넌트 설정값
    sliderTitle,
    showTitle,
    titleFontSize,
    titleFontWeight, 
    titleColor,
    showPrice,
    showStock,
    
    // 편집 모드 관련
    isEditingTitle,
    titleEditValue,
    
    // 필터링 설정값
    include_product_ids,
    exclude_product_ids,
    include_category_ids,
    exclude_category_ids,
    
    // 스타일
    containerStyle,
    titleStyle,
    
    // 참조
    containerRef,
    sliderRef,
    slideRef
  } = data;
  
  // 액션 추출
  const {
    // 무한 슬라이드 이동
    moveSlide,
    
    // 터치/스와이프 이벤트
    handleTouchStart,
    handleTouchMove, 
    handleTouchEnd,
    
    // 상품 클릭
    handleProductClick,
    
    // 제목 편집 관련
    handleTitleDoubleClick,
    handleTitleBlur,
    setTitleEditValue,
    
    // 필터링 설정 렌더링
    renderFilteringSettings,
    
    // 필터링 설정 변경
    handleFilterOptionChange,
    
    // 가격 포맷팅
    formatPrice
  } = actions;
  
  // 로딩 상태 처리
  if (loading) {
    return <div className="product-slider-loading" />;
  }
  
  // 상품이 없는 경우 메시지 표시
  if (!allProducts || allProducts.length === 0) {
    return (
      <div 
        ref={containerRef} 
        className="product-slider-container" 
        style={containerStyle}
      >
        {showTitle && (
          isEditingTitle ? (
            <input
              value={titleEditValue}
              onChange={(e) => setTitleEditValue(e.target.value)}
              onBlur={handleTitleBlur}
              style={{
                ...titleStyle,
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '0 10px',
                margin: '0 0 30px 0'
              }}
              autoFocus
            />
          ) : (
            <h3 
              className="product-slider-title"
              style={titleStyle} 
              onDoubleClick={handleTitleDoubleClick}
            >
              {sliderTitle}
            </h3>
          )
        )}
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {t("표시할 상품이 없습니다.")}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="product-slider-container" 
      style={containerStyle}
    >
      {/* 제목 영역 - 원본과 동일한 편집 기능 */}
      {showTitle && (
        isEditingTitle ? (
          <input
            value={titleEditValue}
            onChange={(e) => setTitleEditValue(e.target.value)}
            onBlur={handleTitleBlur}
            style={{
              ...titleStyle,
              width: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              padding: '0 10px',
              margin: '0 0 30px 0'
            }}
            autoFocus
          />
        ) : (
          <h3 
            className="product-slider-title"
            style={titleStyle}
            onDoubleClick={handleTitleDoubleClick}
          >
            {sliderTitle}
          </h3>
        )
      )}
      
      {/* 이전/다음 버튼 - 원본과 동일한 위치와 스타일 */}
      <button className="product-slider-prev-button" onClick={() => moveSlide('prev')} />
      <button className="product-slider-next-button" onClick={() => moveSlide('next')} />
      
      {/* 슬라이더 마스크 - 무한 슬라이드 컨테이너 */}
      <div className="product-slider-mask">
        <div 
          ref={sliderRef} 
          className={`product-slider-wrapper ${!isTransitioning ? 'no-transition' : ''}`}
          style={{ transform: `translateX(${translateX}px)` }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {allProducts.map((product: any, index: number) => {
            const deviceClass = isMobile ? 'mobile' : isTablet ? 'tablet' : 'pc';
            const hasImage = Boolean(product.image || product.thumbnail || product.config?.img_url);
            
            return (
              <div 
                key={`${product.id}-${index}`}
                className={`product-slider-slide ${deviceClass}`}
                ref={index === 0 ? slideRef : null}
              >
                <div className={`product-slider-item ${hasImage ? 'has-image' : ''}`}>
                  <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {/* 상품 이미지 */}
                    <div className="product-slider-image-container">
                      {React.createElement(SafeImage as any, {
                        src: product.image || product.thumbnail || product.config?.img_url,
                        alt: t(product.title || '') || t(product.name || '') || t('상품명 없음'),
                        style: { 
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        },
                        placeholderStyle: {
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#CCCCCC",
                          position: "absolute",
                          top: 0,
                          left: 0
                        }
                      })}
                    </div>
                    
                    {/* 상품 정보 */}
                    <div className="product-slider-info">
                      <h3 className="product-slider-product-title">
                        {t(product.title || product.name || '') || t('상품명 없음')}
                      </h3>
                      
                      {/* 가격 표시 - 원본과 동일한 할인가격 로직 */}
                      {showPrice && (
                        <div className="product-slider-price">
                          {(() => {
                            const originalPrice = Number(product.price || product.config?.default_price || 0);
                            const salePrice = Number(product.salePrice || product.config?.discounted_price || 0);
                            const hasDiscount = salePrice > 0 && salePrice < originalPrice;
                            
                            if (hasDiscount) {
                              return (
                                <>
                                  {formatPrice(salePrice)}
                                  <span className="product-slider-sale-price">
                                    {formatPrice(originalPrice)}
                                  </span>
                                </>
                              );
                            } else {
                              return formatPrice(originalPrice);
                            }
                          })()} 
                        </div>
                      )}
                      
                      {/* 재고 표시 */}
                      {showStock && product.config?.stock_count !== undefined && (
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                          {t("재고")}: {product.config?.stock_count}{t("개")}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* 필터링 설정 UI - 원본과 동일한 기능 */}
      {renderFilteringSettings && renderFilteringSettings() && (
        <div className="product-slider-filtering-container">
          <h4 className="product-slider-filtering-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '8px'}}>
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
              <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
              <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
            </svg>
            {t("필터링 설정")}
          </h4>
          
          <div className="product-slider-filtering-input-group">
            <label className="product-slider-filtering-label">
              {t("포함할 상품 ID")}
            </label>
            <input 
              type="text" 
              value={include_product_ids || ''}
              onChange={(e) => handleFilterOptionChange('include_product_ids', e.target.value)}
              className="product-slider-filtering-input"
            />
            <div className="product-slider-filtering-help">
              {t("쉼표로 구분하여 입력 (예: 73,74,75)")}
            </div>
          </div>
          
          <div className="product-slider-filtering-input-group">
            <label className="product-slider-filtering-label">
              {t("제외할 상품 ID")}
            </label>
            <input 
              type="text" 
              value={exclude_product_ids || ''}
              onChange={(e) => handleFilterOptionChange('exclude_product_ids', e.target.value)}
              className="product-slider-filtering-input"
            />
            <div className="product-slider-filtering-help">
              {t("쉼표로 구분하여 입력 (예: 74,76)")}
            </div>
          </div>
          
          <div className="product-slider-filtering-input-group">
            <label className="product-slider-filtering-label">
              {t("포함할 카테고리 ID")}
            </label>
            <input 
              type="text" 
              value={include_category_ids || ''}
              onChange={(e) => handleFilterOptionChange('include_category_ids', e.target.value)}
              className="product-slider-filtering-input"
            />
            <div className="product-slider-filtering-help">
              {t("쉼표로 구분하여 입력 (예: 14,8)")}
            </div>
          </div>
          
          <div className="product-slider-filtering-input-group">
            <label className="product-slider-filtering-label">
              {t("제외할 카테고리 ID")}
            </label>
            <input 
              type="text" 
              value={exclude_category_ids || ''}
              onChange={(e) => handleFilterOptionChange('exclude_category_ids', e.target.value)}
              className="product-slider-filtering-input"
            />
            <div className="product-slider-filtering-help">
              {t("쉼표로 구분하여 입력 (예: 16,17)")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicProductSliderSkin;